import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin } from 'lucide-react';
import axios from 'axios';
import { mockData } from './mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { company, industries } = mockData;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    business: '',
    industry: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitType, setSubmitType] = useState(''); // 'success' or 'error'
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear field error when user starts typing
    if (fieldErrors[e.target.name]) {
      setFieldErrors({
        ...fieldErrors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName || formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }
    
    if (!formData.lastName || formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }
    
    if (!formData.email || !formData.email.includes('@')) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.business || formData.business.trim().length < 2) {
      errors.business = 'Business name must be at least 2 characters';
    }
    
    if (!formData.industry) {
      errors.industry = 'Please select your industry';
    }
    
    if (!formData.message || formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitType('');
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setIsSubmitting(false);
      return;
    }
    
    setFieldErrors({});

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setSubmitType('success');
        setSubmitMessage(response.data.message);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          business: '',
          industry: '',
          message: ''
        });
      }
    } catch (error) {
      setSubmitType('error');
      
      if (error.response?.status === 409) {
        setSubmitMessage(error.response.data.detail);
      } else if (error.response?.status === 400) {
        setSubmitMessage('Please check your information and try again.');
      } else {
        setSubmitMessage('Something went wrong. Please try again or contact us directly.');
      }
      
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">Ready to Automate Your Business?</h2>
          <p className="body-large max-w-2xl mx-auto">
            Get started with a free consultation. We'll assess your current operations 
            and show you exactly how AI can transform your business.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="heading-2 mb-4">Get in Touch</h3>
              <p className="body-medium text-gray-600 mb-6">
                Ready to transform your business with AI? Let's discuss how our automation 
                solutions can save you time and increase your revenue.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Phone size={20} className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium mb-1">Phone</h4>
                  <p className="text-gray-600">{company.phone}</p>
                  <p className="text-sm text-gray-500">Available 9 AM - 6 PM EST</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Mail size={20} className="text-orange-600" />
                </div>
                <div>
                  <h4 className="font-medium mb-1">Email</h4>
                  <p className="text-gray-600">{company.email}</p>
                  <p className="text-sm text-gray-500">We respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <MapPin size={20} className="text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium mb-1">Address</h4>
                  <p className="text-gray-600">{company.address}</p>
                  <p className="text-sm text-gray-500">Serving businesses nationwide</p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-medium mb-4">Why Choose AIGENTO?</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">500+</div>
                  <div className="text-sm text-gray-600">Businesses Automated</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">80%</div>
                  <div className="text-sm text-gray-600">Avg. Time Savings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">24/7</div>
                  <div className="text-sm text-gray-600">AI Agent Availability</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">30</div>
                  <div className="text-sm text-gray-600">Day Guarantee</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="voice-card">
            <h3 className="voice-card-title mb-2">Schedule Your Free Consultation</h3>
            <p className="voice-card-description mb-6">
              Fill out the form below and we'll get back to you with a customized 
              automation plan for your business.
            </p>

            {submitMessage && (
              <div className={`px-4 py-3 rounded-lg mb-6 ${
                submitType === 'success' 
                  ? 'bg-green-50 border border-green-200 text-green-800' 
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {submitMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`form-input ${fieldErrors.firstName ? 'border-red-500' : ''}`}
                    required
                  />
                  {fieldErrors.firstName && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors.firstName}</p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`form-input ${fieldErrors.lastName ? 'border-red-500' : ''}`}
                    required
                  />
                  {fieldErrors.lastName && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.email ? 'border-red-500' : ''}`}
                  required
                />
                {fieldErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.email}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Business Name *</label>
                <input
                  type="text"
                  name="business"
                  value={formData.business}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.business ? 'border-red-500' : ''}`}
                  required
                />
                {fieldErrors.business && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.business}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Industry *</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.industry ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="">Select your industry</option>
                  {industries.map((industry, index) => (
                    <option key={index} value={industry.name}>
                      {industry.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.industry && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.industry}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Tell us about your business needs *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={`form-textarea ${fieldErrors.message ? 'border-red-500' : ''}`}
                  placeholder="Describe your current challenges and what you'd like to automate..."
                  required
                ></textarea>
                {fieldErrors.message && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full hover-scale"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send size={16} className="ml-2" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;